import { Button, ButtonGroup, Container, Grid, TextField } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import IAdminTag from "../../../models/IAdminTag";
import TagServiceInstance from "../../../services/TagService";
import OntimeTable from "../../common/OntimeTable";
import EditTagDialog from "./EditTagDialog";
import OntimeBreadcrumb from "../../common/OntimeBreadcrumb";

const useStyles = makeStyles((theme) => ({
    table: {
        tableLayout: 'auto'
    },
    content: {
        flexGrow: 1,
        height: '100vh',
        overflow: 'auto',
    },
    appBarSpacer: theme.mixins.toolbar,
    container: {
        paddingTop: theme.spacing(4),
        paddingBottom: theme.spacing(4),
    },
    paper: {
        padding: theme.spacing(2),
        display: 'flex',
        overflow: 'auto',
        flexDirection: 'column',
    }
}));

export default function AdminTagManagement() {
    const [tags, setTags] = useState<IAdminTag[]>([]);
    const [editingTag, setEditingTag] = useState<IAdminTag>();
    const [tagCount, setTagCount] = useState(0);
    const classes = useStyles();
    const { t } = useTranslation("common");
    const columns = [
        { id: 'tagNumber', numeric: true, label: t("adminTagManagement.tagNumber"), searchable: true, rowId: true },
        { id: 'assignedToName', numeric: false, searchable: true, label: t("adminTagManagement.assignedTo") },
        { id: 'pin', numeric: true, disablePadding: true, label: t("adminTagManagement.pin"), searchable: false }
    ];
    const searchTag = (term: string) => {
        if (term !== '') {
            if (isNaN(+term) === false) {
                if (term.length > 4) {
                    TagServiceInstance.searchTagsByTagNumber(term).then(response => {
                        setTags(response);
                    });
                }
            } else {
                setTags([]);
            }
        }
    }

    const handleAdd = () => {
        TagServiceInstance.getNextTag().then(response => {
            const newTag: IAdminTag = response;
            setEditingTag(newTag);
        }).catch(reason => {
        });

    }

    const onGenerateTags = () => {
        TagServiceInstance.generateTags(tagCount).then(() => {
            alert('done?');
        })
            .catch(reason => {
                console.error('There is an error generating tags', reason);
            });
    }

    const handleDelete = async (tagNumbers: string[]) => {
        tagNumbers.forEach(async (tagNumber) => {
            await TagServiceInstance.deleteTag(tagNumber);
        });
        const updatedTags = [...tags.filter(tag => !tagNumbers.includes(tag.tagNumber))];
        setTags(updatedTags);
    }

    const handleEdit = (tagNumber: string) => {
        if (tags) {
            const i = tags.findIndex(c => c.tagNumber === tagNumber);
            if (i !== -1) {
                setEditingTag(tags[i]);
            }
        }
    }

    const handleCompleteEdit = async (tag: IAdminTag) => {
        let savedTag = await TagServiceInstance.upsertTag(tag);
        const updatedTags = [...tags.filter(c => c.tagNumber !== tag.tagNumber), savedTag];
        setTags(updatedTags);
    }

    return (
        <main className={classes.content}>
            <div className={classes.appBarSpacer} />
            {editingTag === undefined ? null : (<EditTagDialog
                tag={editingTag}
                onCompleteEdit={handleCompleteEdit}
                onCancelEdit={() => setEditingTag(undefined)} />)}
            <Container maxWidth="lg" className={classes.container}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <OntimeBreadcrumb nameMap={{}} current={t('adminTagManagement.tableTitle')} />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            value={tagCount}
                            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                            onChange={(event) => setTagCount(parseInt(event.target.value))} />
                        <ButtonGroup>
                            <Button>{t("adminTagManagement.generateTags")}</Button>
                            <Button>{t("adminTagManagement.importTags")}</Button>
                            <Button onClick={(event) => onGenerateTags()}>{t("adminTagManagement.generateAndImportTags")}</Button>
                        </ButtonGroup>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            id="tag-search"
                            label={t("adminTagManagement.search")}
                            type="search"
                            onChange={(e) => searchTag(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <OntimeTable
                            tableItems={tags}
                            columns={columns}
                            defaultOrderColumn={"tagNumber"}
                            dense={false}
                            title={t("adminTagManagement.tableTitle")}
                            editable={true}
                            onAdd={handleAdd}
                            onDelete={handleDelete}
                            onEdit={handleEdit}
                        />
                    </Grid>
                </Grid>
            </Container>
        </main>
    );
}
